import formatMessage from 'format-message';
import {requestSpeechAudio} from './api';
import TTS2AudioPlayer from './audio-player';
import {
    createCacheKey,
    getCachedAudioUrl,
    setCachedAudioUrl
} from './cache';

const BlockType = {
    COMMAND: 'command'
};

const ArgumentType = {
    STRING: 'string',
    NUMBER: 'number'
};

const COLOR_PRIMARY = '#0F8B8D';
const COLOR_SECONDARY = '#0B6E70';
const COLOR_TERTIARY = '#084F51';

const normaliseText = value => String(value || '').trim();
const normaliseLanguage = value => String(value || 'english').toLowerCase();
const normaliseStyle = value => String(value || 'Normal');
const normaliseSpeed = value => {
    const speed = Number(value);
    if (Number.isNaN(speed)) return 1;
    return Math.max(0.4, Math.min(1.6, speed));
};

class TTS2Extension {
    constructor (runtime) {
        this.runtime = runtime;
        this._style = 'Normal';
        this._speed = 1;
        this._queue = Promise.resolve();
        this._player = new TTS2AudioPlayer();
        this._stopped = false;
        this._playbackToken = 0;

        this.runtime.on('PROJECT_STOP_ALL', () => {
            this.stopSpeaking();
        });
    }

    getInfo () {
        return {
            id: 'tts2',
            name: formatMessage({
                id: 'tts2.extensionName',
                default: 'TTS 2.0',
                description: 'Name for the Gemini-powered text to speech extension'
            }),
            color1: COLOR_PRIMARY,
            color2: COLOR_SECONDARY,
            color3: COLOR_TERTIARY,
            blocks: [
                {
                    opcode: 'speak',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'tts2.blocks.speak',
                        default: 'speak [TEXT]'
                    }),
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Hello, let us learn together!'
                        }
                    }
                },
                {
                    opcode: 'speakInLanguage',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'tts2.blocks.speakInLanguage',
                        default: 'speak [TEXT] in [LANGUAGE]'
                    }),
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Welcome to class'
                        },
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languageMenu',
                            defaultValue: 'english'
                        }
                    }
                },
                '---',
                {
                    opcode: 'stopSpeaking',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'tts2.blocks.stopSpeaking',
                        default: 'stop speaking'
                    })
                },
                {
                    opcode: 'setSpeed',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'tts2.blocks.setSpeed',
                        default: 'set speaking speed to [SPEED]'
                    }),
                    arguments: {
                        SPEED: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1
                        }
                    }
                },
                {
                    opcode: 'setStyle',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'tts2.blocks.setStyle',
                        default: 'set voice style to [STYLE]'
                    }),
                    arguments: {
                        STYLE: {
                            type: ArgumentType.STRING,
                            menu: 'styleMenu',
                            defaultValue: 'Normal'
                        }
                    }
                }
            ],
            menus: {
                languageMenu: {
                    acceptReporters: true,
                    items: [
                        {text: 'English', value: 'english'},
                        {text: 'Hindi', value: 'hindi'},
                        {text: 'Odia', value: 'odia'},
                        {text: 'Bengali', value: 'bengali'},
                        {text: 'Tamil', value: 'tamil'},
                        {text: 'Telugu', value: 'telugu'},
                        {text: 'Kannada', value: 'kannada'},
                        {text: 'Malayalam', value: 'malayalam'},
                        {text: 'Marathi', value: 'marathi'},
                        {text: 'Gujarati', value: 'gujarati'},
                        {text: 'Punjabi', value: 'punjabi'},
                        {text: 'Assamese', value: 'assamese'}
                    ]
                },
                styleMenu: {
                    acceptReporters: true,
                    items: [
                        'Normal',
                        'Friendly Teacher',
                        'Story Teller',
                        'Slow Learning',
                        'Child Friendly'
                    ]
                }
            }
        };
    }

    speak (args) {
        return this._enqueueSpeech({
            text: normaliseText(args.TEXT),
            language: 'english',
            style: this._style,
            speed: this._speed
        });
    }

    speakInLanguage (args) {
        return this._enqueueSpeech({
            text: normaliseText(args.TEXT),
            language: normaliseLanguage(args.LANGUAGE),
            style: this._style,
            speed: this._speed
        });
    }

    stopSpeaking () {
        this._stopped = true;
        this._playbackToken++;
        this._player.stop();
        this._queue = Promise.resolve();
    }

    setSpeed (args) {
        this._speed = normaliseSpeed(args.SPEED);
    }

    setStyle (args) {
        this._style = normaliseStyle(args.STYLE);
    }

    _enqueueSpeech (request) {
        this._stopped = false;
        const playbackToken = this._playbackToken;
        this._queue = this._queue
            .catch(() => null)
            .then(() => this._speakRequest(request, playbackToken));
        return this._queue;
    }

    async _speakRequest (request, playbackToken) {
        if (!request.text || this._stopped || playbackToken !== this._playbackToken) return;

        try {
            const cacheKey = await createCacheKey(request);
            let audioUrl = getCachedAudioUrl(cacheKey);
            if (!audioUrl) {
                const response = await requestSpeechAudio(request);
                audioUrl = response && response.audioUrl;
                setCachedAudioUrl(cacheKey, audioUrl);
            }

            if (audioUrl && !this._stopped && playbackToken === this._playbackToken) {
                await this._player.playUrl(audioUrl);
                return;
            }
        } catch (error) {
            // Gemini or the local service may be offline in classrooms.
            // Fall through to browser speech so student projects keep running.
            // eslint-disable-next-line no-console
            console.warn('[TTS 2.0] Using browser speech fallback:', error);
        }

        if (!this._stopped && playbackToken === this._playbackToken) {
            await this._player.speakWithBrowser(
                request.text,
                request.language,
                request.speed
            );
        }
    }
}

export default TTS2Extension;
